import { createContext } from 'react';
import type { Column } from './tableColumns';

/**
 * One text filter for every table on the page.
 *
 * Set once, above the reports, and read by `DataTable` itself — so every table
 * the reports draw narrows together, including the ones inside a card, and no
 * report has to wire it through. Empty means no filter.
 */
export const TableFilterContext = createContext<string>('');

/** The words typed, lower case. Pure. */
export function filterWords(text: string): string[] {
  return (text || '').toLowerCase().split(/\s+/).filter(Boolean);
}

/** Whether a row has every word somewhere in its columns. Pure.
 *
 *  Reads each column's `value` — what the CSV gets and what sorting compares —
 *  so it matches what the table says, not how a cell happens to be drawn. */
export function matchesFilter<T>(row: T, columns: Column<T>[], words: string[]): boolean {
  if (!words.length) return true;
  const hay = columns
    .map(c => {
      try {
        const v = c.value(row);
        return v === null || v === undefined ? '' : String(v);
      } catch {
        return '';
      }
    })
    .join(' \u0001 ')
    .toLowerCase();
  return words.every(w => hay.includes(w));
}

/** Whether one line of text has every word. Pure. */
export function textMatches(text: string | null | undefined, words: string[]): boolean {
  if (!words.length) return true;
  const hay = (text || '').toLowerCase();
  return words.every(w => hay.includes(w));
}

/** One board row as text to search: its title and the number beside it. */
export function previewText(r: { title: string; note?: string | null }): string {
  return `${r.title} ${r.note ?? ''}`;
}

/** "3 of 20 match", or "no match": of the rows in the card's whole table. */
export function matchLine(m: { hits: number; seen: number }): string {
  return m.hits ? `${m.hits} of ${m.seen} match` : 'no match';
}

/** The box itself. Escape or × clears it. */
export function TableFilterBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative" data-testid="table-filter">
      <input
        type="search"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape') onChange('');
        }}
        placeholder="Filter…"
        aria-label="Filter every table and card by text"
        title="Keep the rows that mention every word you type, on every table and card in the room. It stays on as you move around; × clears it."
        // LIT WHILE ON: a filter you forgot about would quietly hide rows.
        className={`w-40 rounded border px-2 py-0.5 pr-5 text-xs dark:text-gray-100 ${
          value
            ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-300 dark:border-amber-500 dark:bg-amber-900/30'
            : 'border-gray-300 dark:border-gray-600 dark:bg-gray-700'
        }`}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear the filter"
          className="icon-btn absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          ×
        </button>
      )}
    </div>
  );
}
